import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { ReplenishmentClient } from "@/features/warehouse/ReplenishmentClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";

// Warehouse → Aanvullen (docs/design_handoff_wms_mw8/README.md §5, BO-Aanvullen): the pick places that are
// op and what was replenished today. Reading is wms_stock, as Voorraad.
export const Route = createFileRoute("/_shell/warehouse/replenishment")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_stock") && !hasModule(context.session, "wms_stock_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): { warehouse?: string } => ({
    warehouse: typeof search.warehouse === "string" && search.warehouse ? search.warehouse : undefined,
  }),
  component: ReplenishmentScreen,
});

function ReplenishmentScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );
  const choice = useWarehouseChoice(search.warehouse, (code) => updateParams({ warehouse: code }));
  const warehouse = choice.current;

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Warehouse Replenishment">
      {choice.error ? (
        <div className="wl-alert wl-alert--danger">
          <span>{choice.error instanceof Error ? choice.error.message : t("common.errorGeneric")}</span>
        </div>
      ) : !warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : (
        <ReplenishmentClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          userId={session.userId}
        />
      )}
    </div>
  );
}
